import React from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { LogIn, Shield, Zap, BarChart3, Cloud, Lock, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

export const PlataformaPage = () => {
  const handleAccess = () => {
    toast.info('Redirigiendo a la plataforma...');
  };

  const features = [
    {
      icon: Shield,
      title: 'Seguridad Certificada',
      description: 'Protección de datos con encriptación de nivel bancario SSL 256-bit y cumplimiento GDPR.'
    },
    {
      icon: Zap,
      title: 'Acceso Instantáneo',
      description: 'Disponible 24/7 desde cualquier dispositivo con sincronización en tiempo real.'
    },
    {
      icon: BarChart3,
      title: 'Dashboard Completo',
      description: 'Visualiza todas tus métricas, reportes y estadísticas en un solo lugar.'
    },
    {
      icon: Cloud,
      title: 'Almacenamiento en la Nube',
      description: 'Todos tus datos seguros y accesibles desde cualquier lugar del mundo.'
    },
    {
      icon: Lock,
      title: 'Control de Acceso',
      description: 'Gestiona permisos de usuario y mantén tu información protegida con roles personalizados.'
    },
    {
      icon: Smartphone,
      title: 'Multi-Dispositivo',
      description: 'Funciona perfectamente en computadoras, tablets y smartphones sin instalaciones.'
    }
  ];

  const plans = [
    {
      name: 'Básico',
      price: 'Gratis',
      features: ['Hasta 50 transacciones/mes', 'Dashboard básico', 'Soporte por email', 'Almacenamiento 5GB']
    },
    {
      name: 'Profesional',
      price: '$49/mes',
      features: ['Transacciones ilimitadas', 'Analytics avanzado', 'Soporte prioritario', 'Almacenamiento 100GB', 'API Access'],
      popular: true
    },
    {
      name: 'Empresarial',
      price: 'Contactar',
      features: ['Todo lo del plan profesional', 'Servidor dedicado', 'Soporte 24/7', 'Almacenamiento ilimitado', 'White label']
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-cyan-900/20 to-slate-900"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-4 py-2 mb-6">
            <span className="text-cyan-300 text-sm font-medium">PLATAFORMA WEB</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Accede a tu
            <br />
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Panel de Control VERSO
            </span>
          </h1>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto mb-12">
            Gestiona tu negocio desde cualquier lugar con nuestra plataforma web. 
            Todas las funcionalidades sin necesidad de instalaciones.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleAccess}
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-12 py-6 text-lg rounded-xl shadow-lg"
            >
              <LogIn className="mr-2" size={20} />
              Acceder a la Plataforma
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/10 px-12 py-6 text-lg rounded-xl"
            >
              Ver Demo en Vivo
            </Button>
          </div>

          <p className="text-gray-400 text-sm mt-6">
            ¿No tienes cuenta?{' '}
            <button className="text-cyan-400 hover:text-cyan-300 transition-colors font-semibold">
              Regístrate gratis aquí
            </button>
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="relative py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Potencia y Simplicidad</h2>
            <p className="text-gray-400 text-lg">Herramientas profesionales al alcance de tu mano</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="bg-white/5 backdrop-blur-sm border-cyan-500/20 p-6 hover:border-cyan-500/40 transition-all duration-300 group">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="text-cyan-400" size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm">{feature.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="relative py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Planes Flexibles</h2>
            <p className="text-gray-400 text-lg">Elige el plan que mejor se adapte a tu negocio</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <Card key={index} className={`p-8 ${plan.popular ? 'bg-gradient-to-b from-cyan-500/20 to-transparent border-cyan-500/40 scale-105' : 'bg-white/5 border-cyan-500/20'} backdrop-blur-sm transition-all duration-300 hover:scale-105`}>
                {plan.popular && (
                  <div className="inline-block bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-4">
                    MÁS POPULAR
                  </div>
                )}
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <div className="text-4xl font-bold text-cyan-400 mb-6">{plan.price}</div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-2 text-gray-300">
                      <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
                      </div>
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button className={`w-full ${plan.popular ? 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600' : 'bg-white/10 hover:bg-white/20'} text-white rounded-xl py-6`}>
                  {plan.price === 'Contactar' ? 'Contactar Ventas' : 'Comenzar Ahora'}
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-cyan-500/30 backdrop-blur-sm p-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              ¿Listo para Transformar tu Negocio?
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              Únete a más de 15,000 negocios que ya confían en VERSO
            </p>
            <Button 
              onClick={handleAccess}
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-12 py-6 text-lg rounded-xl"
            >
              Comenzar Gratis por 30 Días
            </Button>
          </Card>
        </div>
      </section>
    </div>
  );
};